/**
 * AvatarCropDialog — lets a user zoom and drag a chosen photo inside a round
 * frame so they can center their face before it's saved as their avatar.
 * Outputs a 320x320 PNG blob via <canvas> and hands it to onCropped().
 */
import React, { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, ZoomIn, ZoomOut } from 'lucide-react';

const OUT = 320;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function cropToBlob(src, area) {
  const image = await loadImage(src);
  const canvas = document.createElement('canvas');
  canvas.width = OUT;
  canvas.height = OUT;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, OUT, OUT);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png', 0.92));
}

export default function AvatarCropDialog({ open, imageSrc, onCancel, onCropped }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onComplete = useCallback((_area, px) => setAreaPixels(px), []);

  const confirm = async () => {
    if (!areaPixels) return;
    setBusy(true);
    try {
      const blob = await cropToBlob(imageSrc, areaPixels);
      if (blob) await onCropped(blob);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onCancel(); }}>
      <DialogContent className="max-w-sm" data-testid="avatar-crop-dialog">
        <DialogHeader><DialogTitle className="text-sm">Adjust your photo</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Zoom and drag your photo to center your face, then save it as your avatar.</DialogDescription>
        <div className="relative w-full h-64 bg-black/70 rounded-lg overflow-hidden">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              minZoom={1}
              maxZoom={4}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onComplete}
            />
          )}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <ZoomOut className="w-4 h-4 text-muted-foreground" />
          <input type="range" min={1} max={4} step={0.01} value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 accent-primary" data-testid="avatar-zoom-slider" aria-label="Zoom" />
          <ZoomIn className="w-4 h-4 text-muted-foreground" />
        </div>
        <p className="text-[11px] text-muted-foreground">Drag the photo to reposition and slide to zoom — center your face in the circle.</p>
        <div className="flex justify-end gap-2 mt-1">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={busy} data-testid="avatar-crop-cancel">Cancel</Button>
          <Button size="sm" onClick={confirm} disabled={busy || !areaPixels} data-testid="avatar-crop-save">
            {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Use photo
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
